import { PopupBody, PopupHeader, PopupWrapper } from '@/components'
import { ModalLayout } from '@/components/HeadlessUI'
import { MODAL_PANEL_CLASS_MD, MODAL_PLACEMENT_CENTER } from '@/constants'
import { formatHelper } from '@/helpers/format.helper'
import { messageHelper } from '@/helpers/message.helper'
import { licenseAdminApi } from '@/services/licenseAdminApi'
import { LicenseTypeTypes, type TenantLicenseListResponse, type TenantSubscriptionTimelineResponse } from '@/services/licenseTypes'
import { AnimationSkeleton } from '@/pages/ui/Skeleton'
import { useEffect, useState } from 'react'

interface Props {
	license: TenantLicenseListResponse | null
	onClose: () => void
}

const dateLabel = (value: string) => new Date(value).toLocaleDateString()

const formatPackUnits = (userCount?: number, instanceCount?: number, databaseCount?: number) =>
	[
		(userCount ?? 0) > 0 ? `+${userCount} user(s)` : null,
		(instanceCount ?? 0) > 0 ? `+${instanceCount} instance(s)` : null,
		(databaseCount ?? 0) > 0 ? `+${databaseCount} database(s)` : null,
	]
		.filter(Boolean)
		.join(', ')

const timelineStatusClass = (status: string) => {
	const normalized = status.toLowerCase()
	if (normalized === 'current') return 'bg-emerald-500/10 text-emerald-500 ring-emerald-500/20'
	if (normalized === 'upcoming') return 'bg-sky-500/10 text-sky-500 ring-sky-500/20'
	if (normalized === 'cancelled' || normalized === 'suspended') return 'bg-red-500/10 text-red-500 ring-red-500/20'
	if (normalized === 'pending payment') return 'bg-amber-500/10 text-amber-500 ring-amber-500/20'
	return 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] ring-[hsl(var(--border))]'
}

const SubscriptionTimelineModal = ({ license, onClose }: Props) => {
	const [rows, setRows] = useState<TenantSubscriptionTimelineResponse[]>([])
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		if (!license) return

		setLoading(true)
		licenseAdminApi
			.getTimeline(license.id)
			.then(setRows)
			.catch((e) => {
				messageHelper.showError(String(e))
				setRows([])
			})
			.finally(() => setLoading(false))
	}, [license])

	return (
		<ModalLayout showModal={!!license} toggleModal={onClose} panelClassName={MODAL_PANEL_CLASS_MD} placement={MODAL_PLACEMENT_CENTER}>
			{license && (
				<PopupWrapper>
					<PopupHeader title={`Subscription timeline - ${license.tenantName}`} onClose={onClose} />
					<PopupBody>
						{loading ? (
							<AnimationSkeleton />
						) : rows.length === 0 ? (
							<div className="rounded-lg border border-dashed border-[hsl(var(--border))] p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">No subscription timeline found.</div>
						) : (
							<div className="space-y-3">
								{rows.map((row) => (
									<div key={row.tenantSubscriptionPlanId} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-4">
										<div className="flex flex-wrap items-start justify-between gap-3">
											<div>
												<div className="flex flex-wrap items-center gap-2">
													<h4 className="text-sm font-semibold text-[hsl(var(--foreground))]">{row.planName}</h4>
													<span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${timelineStatusClass(row.timelineStatus)}`}>{row.timelineStatus}</span>
												</div>
												<p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{row.licenseType === LicenseTypeTypes.Trial ? 'Trial' : 'Paid'} subscription</p>
											</div>
											<div className="text-right text-sm font-semibold text-[hsl(var(--foreground))]">{formatHelper.planPrice(row.price)}</div>
										</div>

										<div className="mt-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-5">
											<div>
												<p className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Starts</p>
												<p>{dateLabel(row.startedOn)}</p>
											</div>
											<div>
												<p className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Expires</p>
												<p>{dateLabel(row.expiresOn)}</p>
											</div>
											<div>
												<p className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Users</p>
												<p>{row.maxUsers ?? '—'}</p>
											</div>
											<div>
												<p className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Instances</p>
												<p>{row.maxInstances ?? '—'}</p>
											</div>
											<div>
												<p className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Databases</p>
												<p>{row.maxDatabases ?? '—'}</p>
											</div>
										</div>

										{(row.packPurchases ?? []).length > 0 && (
											<div className="mt-4 border-t border-[hsl(var(--border))] pt-4">
												<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
													<p className="text-xs font-semibold uppercase text-[hsl(var(--muted-foreground))]">Pack history</p>
													<span className="inline-flex items-center rounded-full bg-[hsl(var(--muted))] px-2.5 py-1 text-xs font-semibold text-[hsl(var(--muted-foreground))] ring-1 ring-inset ring-[hsl(var(--border))]">
														{row.packPurchases.length}
													</span>
												</div>
												<div className="space-y-2">
													{row.packPurchases.map((pack) => (
														<div key={pack.id} className="rounded-md border border-[hsl(var(--border))] p-3">
															<div className="flex flex-wrap items-start justify-between gap-3">
																<div className="min-w-0">
																	<p className="text-sm font-semibold text-[hsl(var(--foreground))]">{pack.packName}</p>
																	<p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
																		{formatPackUnits(pack.userCount, pack.instanceCount, pack.databaseCount) || 'Pack purchased'}
																	</p>
																	
																</div>
																<div className="shrink-0 text-right">
																	<p className="text-sm font-semibold text-[hsl(var(--foreground))]">${Number(pack.amount ?? 0).toFixed(2)}</p>
																	<p className="text-xs uppercase text-[hsl(var(--muted-foreground))]">{pack.currency}</p>
																	<p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{dateLabel(pack.purchasedOn)}</p>
																</div>
															</div>
														</div>
													))}
												</div>
											</div>
										)}
									</div>
								))}
							</div>
						)}
					</PopupBody>
				</PopupWrapper>
			)}
		</ModalLayout>
	)
}

export default SubscriptionTimelineModal
